import React, { useState } from "react";
import {
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Menu,
  MenuItem,
} from "@mui/material";
import { FaColumns, FaDownload, FaSyncAlt } from "react-icons/fa";

/**
 * Toolbar shown above any list table.
 *
 * Props from useTableSettings: pageSize, setPageSize, pageSizeOptions,
 *   total, page, visibleColumnKeys, allColumns, toggleColumn, resetColumns.
 *
 * Download props:
 *   onDownload(format: 'csv'|'json'|'excel') — called when user picks a format.
 *   downloadLoading — shows spinner on the download button while fetching.
 */
const TableToolbar = ({
  pageSize,
  setPageSize,
  pageSizeOptions = [10, 25, 50, 100],
  total,
  page = 1,
  visibleColumnKeys,
  allColumns,
  toggleColumn,
  resetColumns,
  rightSlot,
  onDownload,
  downloadLoading = false,
  onRefresh,
  refreshLoading = false,
}) => {
  const [columnAnchorEl, setColumnAnchorEl] = useState(null);
  const [downloadAnchorEl, setDownloadAnchorEl] = useState(null);

  const columnMenuOpen = Boolean(columnAnchorEl);
  const downloadMenuOpen = Boolean(downloadAnchorEl);

  const showColumns =
    Array.isArray(allColumns) &&
    allColumns.length > 0 &&
    Array.isArray(visibleColumnKeys) &&
    typeof toggleColumn === "function";

  const handleDownloadSelect = (format) => {
    setDownloadAnchorEl(null);
    onDownload?.(format);
  };

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "8px",
        marginBottom: "10px",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          fontSize: "13px",
          color: "#555",
        }}
      >
        <span>Rows per page:</span>
        <select
          value={pageSize}
          onChange={(e) => setPageSize(parseInt(e.target.value, 10))}
          style={{
            padding: "4px 8px",
            borderRadius: 4,
            border: "1px solid #ced4da",
            background: "#fff",
            fontSize: 13,
          }}
        >
          {pageSizeOptions.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
        {total !== undefined && total !== null && (
          <span style={{ marginLeft: 12 }}>
            {total > 0
              ? `Showing ${(page - 1) * pageSize + 1}–${Math.min(
                  page * pageSize,
                  total,
                )} of ${total} (Page ${page} of ${Math.max(
                  1,
                  Math.ceil(total / pageSize),
                )})`
              : "0 results"}
          </span>
        )}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {rightSlot}

        {typeof onRefresh === "function" && (
          <Button
            variant="outlined"
            size="small"
            startIcon={
              refreshLoading ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <FaSyncAlt />
              )
            }
            disabled={refreshLoading}
            onClick={() => onRefresh()}
          >
            {refreshLoading ? "Refreshing…" : "Refresh"}
          </Button>
        )}

        {typeof onDownload === "function" && (
          <>
            <Button
              variant="outlined"
              size="small"
              startIcon={
                downloadLoading ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <FaDownload />
                )
              }
              disabled={downloadLoading}
              onClick={(e) => setDownloadAnchorEl(e.currentTarget)}
            >
              {downloadLoading ? "Downloading…" : "Download"}
            </Button>

            <Menu
              anchorEl={downloadAnchorEl}
              open={downloadMenuOpen}
              onClose={() => setDownloadAnchorEl(null)}
              PaperProps={{ sx: { minWidth: 160 } }}
            >
              <MenuItem onClick={() => handleDownloadSelect("csv")}>
                CSV (.csv)
              </MenuItem>
              <MenuItem onClick={() => handleDownloadSelect("excel")}>
                Excel (.xlsx)
              </MenuItem>
              <MenuItem onClick={() => handleDownloadSelect("json")}>
                JSON (.json)
              </MenuItem>
            </Menu>
          </>
        )}

        {showColumns && (
          <>
            <Button
              variant="outlined"
              size="small"
              startIcon={<FaColumns />}
              onClick={(e) => setColumnAnchorEl(e.currentTarget)}
            >
              Columns ({visibleColumnKeys.length}/{allColumns.length})
            </Button>

            <Menu
              anchorEl={columnAnchorEl}
              open={columnMenuOpen}
              onClose={() => setColumnAnchorEl(null)}
              PaperProps={{
                sx: {
                  maxHeight: 420,
                  width: 260,
                },
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "6px 12px",
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                <span>Show columns</span>
                {typeof resetColumns === "function" && (
                  <a
                    role="button"
                    onClick={resetColumns}
                    style={{
                      fontSize: 12,
                      color: "#1976d2",
                      cursor: "pointer",
                    }}
                  >
                    Reset
                  </a>
                )}
              </div>
              <Divider />
              {allColumns.map((col) => (
                <MenuItem key={col.key} dense sx={{ py: 0 }}>
                  <FormControlLabel
                    sx={{ m: 0, width: "100%" }}
                    control={
                      <Checkbox
                        size="small"
                        checked={visibleColumnKeys.includes(col.key)}
                        onChange={() => toggleColumn(col.key)}
                      />
                    }
                    label={
                      <span style={{ fontSize: 13 }}>{col.title}</span>
                    }
                  />
                </MenuItem>
              ))}
            </Menu>
          </>
        )}
      </div>
    </div>
  );
};

export default TableToolbar;
